import {
  ComplianceProgramStep,
  GetComplianceProgramLoadPreviewQuery,
} from "types/graphql-schema";

export type LoadPreview =
  GetComplianceProgramLoadPreviewQuery["complianceProgramLoadPreview"];
export type Writer = LoadPreview["writers"][number];

/**
 * How long one question takes, until the admin says otherwise. A guess the admin
 * makes, not a figure the backend knows, so it stays on the screen and never
 * reaches the query. Three minutes, because most review questions are a rating
 * with a comment, and writing the comment is most of the time.
 */
export const defaultSecondsPerQuestion = 180;

/** The minutes the admin typed, as seconds, or null when they are not a time. */
export const parseMinutesPerQuestion = (text: string): number | null => {
  const minutes = Number(text);
  // Checked after rounding, so a value under half a second is not read as zero.
  const seconds = Math.round(minutes * 60);
  return text.trim() !== "" && Number.isFinite(minutes) && seconds > 0
    ? seconds
    : null;
};

/** The hourly cost in the box until the admin types their own, in dollars. */
export const defaultHourlyCost = 100;

/**
 * The steps Generate Context fills from work tools: aggregate_context shares
 * them only with the person and their managers, so a peer or upward reviewer
 * never sees them.
 */
export const toolSteps: ComplianceProgramStep[] = [
  ComplianceProgramStep.SelfReview,
  ComplianceProgramStep.DownwardReview,
];

/**
 * How much of the self and manager writing connecting every work tool would save,
 * until the admin types their own. A guess, shown in a box like the time per
 * question, until there is a measured figure to put there instead.
 */
export const defaultToolSavingsPercent = 30;

/** The percentage the admin typed, or null when it is not one. */
export const parsePercent = (text: string): number | null => {
  const percent = Number(text.replace(/[%\s]/g, ""));
  return text.trim() !== "" &&
    Number.isFinite(percent) &&
    percent > 0 &&
    percent <= 100
    ? percent
    : null;
};

/**
 * Today, the saving and what is left, rounded to the minute and the dollar
 * before they are subtracted, so the three lines always add up as shown.
 * Subtracting first and rounding after can put "$875 − $83 = $793" on screen.
 */
export const savingsSums = (
  totalSeconds: number,
  savedSeconds: number,
  hourlyCost: number | null,
) => {
  const totalMinutes = Math.round(totalSeconds / 60);
  const savedMinutes = Math.round(savedSeconds / 60);
  const dollars = (seconds: number) =>
    hourlyCost === null ? null : Math.round((seconds / 3600) * hourlyCost);
  const totalDollars = dollars(totalSeconds);
  const savedDollars = dollars(savedSeconds);
  return {
    totalMinutes,
    savedMinutes,
    afterMinutes: totalMinutes - savedMinutes,
    totalDollars,
    savedDollars,
    afterDollars:
      totalDollars === null || savedDollars === null
        ? null
        : totalDollars - savedDollars,
  };
};

/** Answers in the steps work tools can help with, across everybody. */
export const toolHelpedAnswers = (preview: LoadPreview) =>
  preview.steps
    .filter((step) => toolSteps.includes(step.step))
    .reduce((sum, step) => sum + step.answers, 0);

/** Questions one writer answers in the steps work tools can help with. */
export const writerToolHelpedQuestions = (
  writer: Writer,
  preview: LoadPreview,
) =>
  writer.byStep
    .filter((count) => toolSteps.includes(count.step))
    .reduce(
      (sum, count) =>
        sum +
        count.reviews *
          (preview.steps.find((step) => step.step === count.step)
            ?.questionsEach ?? 0),
      0,
    );

/** The hourly cost the admin typed, or null when it is not an amount. */
export const parseHourlyCost = (text: string): number | null => {
  const cost = Number(text.replace(/[$,\s]/g, ""));
  return text.trim() !== "" && Number.isFinite(cost) && cost > 0 ? cost : null;
};

/** "$429", rounded to the dollar, because the time under it is a guess. */
export const formatMoney = (amount: number) =>
  amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

export const formatCount = (count: number) => count.toLocaleString("en-US");

export const plural = (count: number, one: string, many = `${one}s`) =>
  `${formatCount(count)} ${count === 1 ? one : many}`;

/** "7.15", in hours to at most two decimals, for the one total on top. */
export const formatHours = (seconds: number) =>
  (seconds / 3600).toLocaleString("en-US", { maximumFractionDigits: 2 });

export const formatDuration = (seconds: number) => {
  // Nothing to answer is no work, not a little of it.
  if (seconds === 0) {
    return "0 min";
  }
  if (seconds < 30) {
    return "under 1 min";
  }
  // Rounded once, so 59 min 40 s reads "1 h" and never "60 min".
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0
    ? `${formatCount(hours)} h`
    : `${formatCount(hours)} h ${rest} m`;
};
